import { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import Select from "../components/Select";
import usePollutantMetricLayerOpacity from "./usePollutantMetricLayerOpacity";
import useFieldState from "../hooks/useFieldState";
import useEsri from "../hooks/useEsri";
import useForm from "../hooks/useForm";
import { pollutantMetricPopup } from "../popupTemplate";
import { OpacitySlider } from "../esri-components/OpacitySlider";
import { compare } from "../utils";
import {
  DEFAULT_POLLUTANT_METRIC_OPACITY,
  GIS_PM_LAYER_NAME,
  QUERY_DATA_BUTTON_ACTION,
  APP_SETTINGS,
  ARCGIS_SERVICES_ROOT_URL,
} from "../settings";
import { useFormContext } from "react-hook-form";

const layerOptions = {
  id: null,
  url: "",
  outFields: ["*"],
  opacity: DEFAULT_POLLUTANT_METRIC_OPACITY,
  returnExtentOnly: true,
  visible: false,
  overwriteActions: true,
  popupTemplate: {
    title: "{name}",
    content: pollutantMetricPopup,
    actions: [QUERY_DATA_BUTTON_ACTION],
  },
};

const SelectPollutantMetric = () => {
  const {
    geoProcessor,
    addLayer,
    removeLayer,
    setLayerVisible,
    esriLoader,
    removeAllDataLayers,
  } = useEsri();
  const { register } = useFormContext();

  const {
    getSelectedQueryLayer,
    getSelectedYear,
    onPollutantMetricFieldChange,
    onZoneLaListFieldChange,
    getSelectedPollutantMetric,
  } = useForm();

  const [, setFieldDataState] = useFieldState();

  const {
    onOpacityChange,
    opacityBtnStatus,
    opacityLayer,
    setOpacityBtnStatus,
  } = usePollutantMetricLayerOpacity({
    onPollutantMetricFieldChange,
    onZoneLaListFieldChange,
  });

  const [options, setOptions] = useState([]);
  const [alias, setAlias] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!esriLoader) return;

    const buildPollutantMetricLayers = async () => {
      setIsLoading(true);
      const { GP } = APP_SETTINGS.GIS_SERVER;
      const gpServiceUrls = await geoProcessor(GP.LIST_OF_SERVICE_URL);
      const params = {
        year_in: getSelectedYear,
      };
      const pollutantMetricResponse = await gpServiceUrls.execute(params);
      const { aq_comp_zone, aq_comp_la } =
        pollutantMetricResponse.results[0]["value"];

      const pollutant_metric_list = {
        la: aq_comp_la?.sort(compare),
        zone: aq_comp_zone?.sort(compare),
      };

      if (aq_comp_la && aq_comp_zone) {
        setFieldDataState((prevState) => ({
          ...prevState,
          pollutant_metric_list: {
            ...prevState.pollutant_metric_list,
            ...pollutant_metric_list,
          },
        }));

        setOptions(pollutant_metric_list[getSelectedQueryLayer]);
        setAlias(pollutant_metric_list[getSelectedQueryLayer][0]?.alias);
      }
    };

    buildPollutantMetricLayers().then(() => setIsLoading(false));

    return () => {
      /* clean up */
    };
  }, [
    esriLoader,
    geoProcessor,
    getSelectedQueryLayer,
    getSelectedYear,
    setFieldDataState,
  ]);

  useEffect(() => {
    if (alias && alias.includes(getSelectedYear)) {
      const layerOptionsPM = {
        ...layerOptions,
        visible: true,
      };
      //Reset layers before adding new layer
      removeLayer(GIS_PM_LAYER_NAME);
      removeAllDataLayers();
      //Hide query layer
      if (getSelectedQueryLayer) {
        setLayerVisible("la", false);
        setLayerVisible("zone", false);
      }

      setOpacityBtnStatus(false);

      const urlAlias = options.find(
        (pollutantMetric) => pollutantMetric.id === getSelectedPollutantMetric
      )?.alias;

      if(urlAlias || alias){
        addLayer({
          ...layerOptionsPM,
          id: GIS_PM_LAYER_NAME,
          customPopupAttributes: {
            query_layer: getSelectedQueryLayer,
            query_year: getSelectedYear,
          },
          url: `${ARCGIS_SERVICES_ROOT_URL}Hosted/${urlAlias || alias}/FeatureServer/0`,
        });
      }

      return () => {
        /* clean up */
      };
    }
  }, [
    addLayer,
    alias,
    getSelectedQueryLayer,
    getSelectedYear,
    removeAllDataLayers,
    removeLayer,
    setLayerVisible,
    setOpacityBtnStatus,
    options,
    getSelectedPollutantMetric,
  ]);

  return (
    <>
      <div className="flex content-center flex-wrap">
        <div className="w-full md:w-4/5">
          {isLoading ? (
            <>
              <div className="w-full mb-3 h-5">Pollutant Metric</div>
              <div className="rounded w-full mr-2 mb-3 p-2 bg-gray-400 text-black h-10">
                Loading ...
              </div>
            </>
          ) : (
            <Select
              options={options}
              label="Pollutant Metric"
              name="pollutant_metric"
              ref={register}
              setPollutantMetric={(value) => setAlias(value)}
            />
          )}
        </div>
        <div className="w-full md:w-1/5">
          <div className="relative slider-position range-slider">
            <OpacitySlider
              onFinalChange={onOpacityChange}
              btnStatus={opacityBtnStatus}
              defaultOpacity={opacityLayer}
              tooltipFor="opacity_pm_layer"
            />
            <ReactTooltip id="opacity_pm_layer" place="right" effect="solid">
              <span>Pollutant Metric Layer Transparency</span>
            </ReactTooltip>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectPollutantMetric;
